import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback } from 'react';
import {
  isLocalDevelopment,
  isUserLoggedIn,
  switchDevUser,
  getDefaultDevUser,
  ANONYMOUS_USER_ID,
} from '../services/tokenService';
import { clearThumbnailServiceWorkerCache } from '../services/serviceWorkerRegistration';

interface AuthContextType {
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether auth state is still being determined */
  isLoading: boolean;
  /** Trigger sign-in (MSAL popup in prod, switch to default dev user in local) */
  signIn: () => Promise<void>;
  /** Sign out (clear MSAL in prod, switch to anonymous in local) */
  signOut: () => void;
}

const defaultContextValue: AuthContextType = {
  isAuthenticated: false,
  isLoading: true,
  signIn: async () => {},
  signOut: () => {},
};

const AuthContext = createContext<AuthContextType>(defaultContextValue);

export const useAuth = (): AuthContextType => {
  return useContext(AuthContext);
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check auth state on mount
  useEffect(() => {
    let cancelled = false;
    const checkAuth = async () => {
      try {
        const loggedIn = await isUserLoggedIn();
        if (!cancelled) {
          setIsAuthenticated(loggedIn);
        }
      } catch {
        if (!cancelled) {
          setIsAuthenticated(false);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    checkAuth();
    return () => { cancelled = true; };
  }, []);

  const signIn = useCallback(async () => {
    if (isLocalDevelopment()) {
      // In local dev, sign in as the default dev user and reload
      const defaultUser = getDefaultDevUser();
      switchDevUser(defaultUser.id);
      window.location.reload();
      return;
    }

    // Production: use MSAL popup
    const { loginUser } = await import('../services/tokenService');
    const success = await loginUser();
    if (success) {
      setIsAuthenticated(true);
    }
  }, []);

  const signOut = useCallback(() => {
    if (isLocalDevelopment()) {
      void clearThumbnailServiceWorkerCache().finally(() => {
        // In local dev, switch to anonymous and reload
        switchDevUser(ANONYMOUS_USER_ID);
        window.location.reload();
      });
      return;
    }

    const finalizeSignOut = async () => {
      sessionStorage.clear();
      setIsAuthenticated(false);
      await clearThumbnailServiceWorkerCache();
      window.location.reload();
    };

    // Production: call MSAL logoutPopup to clear Azure AD session, then clean up locally
    import('../services/tokenService').then(async ({ logoutUser }) => {
      try {
        await logoutUser();
      } catch (error) {
        console.error('MSAL logout failed, clearing local state anyway', error);
      }
      await finalizeSignOut();
    }).catch(() => {
      // Fallback: clear local state if dynamic import fails
      void finalizeSignOut();
    });
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
